// Browser Team locator helpers (LAT-1501). The physical `$teamId` route noun
// stays; its value is the stable Team slug. Query/API callers keep using the
// immutable team_id from route loader context or resource projections.

import { useParams } from "@tanstack/react-router";
import { teamBrowserRouteParams } from "@/address/families/team";
import { useOptionalTeamRouteIdentity } from "@/hooks/use-team-route-identity";

/** TanStack params for Team-family Links/navigation from a loaded slug. */
export function teamLinkParams(teamSlug: string): { teamId: string } {
  return teamBrowserRouteParams({ teamSlug });
}

/**
 * Read the Team browser locator for Links/navigation.
 *
 * Precedence (LAT-1501):
 * 1. Team layout identity slug (production leaves under `/teams/$teamId`)
 * 2. Current route `$teamId` param (browser slug)
 * 3. Optional `fallbackTeamSlug` (unit tests / non-route hosts)
 */
export function useTeamBrowserParams(fallbackTeamSlug?: string): {
  teamId: string;
} {
  const identity = useOptionalTeamRouteIdentity();
  let fromRoute: string | undefined;
  try {
    const params = useParams({ strict: false }) as { teamId?: string };
    fromRoute =
      typeof params.teamId === "string" && params.teamId.length > 0
        ? params.teamId
        : undefined;
  } catch {
    fromRoute = undefined;
  }

  const teamId = identity?.teamSlug ?? fromRoute ?? fallbackTeamSlug;
  // Empty string is allowed only for non-Team hosts (e.g. unit tests mounting
  // a Team-scoped widget without a router). Production Team leaves always
  // supply layout identity or a route param. Callers must not navigate with
  // empty params.
  return { teamId: teamId ?? "" };
}
